import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ValueType } from 'react-select';

import DateRangeSelector from './form/DateRangeSelector';
import DataPoints from './form/DataPoints';
import Name from './form/Name';
import Buttons from './form/Buttons';

import { PropertyDefinition } from '../../types/PropertyDefinition';
import { SelectionSet } from '../../types/SelectionSet';
import { Asset } from '../../types/Asset';
import { OptionsType } from '../../types/OptionsType';

import { useGetQuery } from 'api/useGetQuery';
import { usePostMutation } from 'api/usePostMutation';
import { ENDPOINTS } from 'api/endpoints';
import { FormInitialData, NullFormInitialData } from 'types/FormInitialData';
import { useDataEnvironment } from 'contexts/DataEnvironmentContext';
import RequiredFieldMarker from 'components/RequiredFieldMarker';
import { Info } from 'components/Icons/Info';
import { showError } from 'utils/showError';
import { showSuccess } from 'utils/showSuccess';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { useQuery } from 'react-query';
import { axiosClient } from 'api/axiosClient';
import { AxiosResponse } from 'axios';
import { usePrevious } from 'hooks/usePrevious';
import PropertySelector from 'components/PropertySelector/PropertySelector';
import { Button } from 'components/Button';
import styled from 'styled-components';
import { routes } from 'routes';
import { isBefore } from 'date-fns';
import { errorMessage } from 'utils/errorMessage';
import AssetLookup from 'components/AssetLookup/AssetLookup';
import assetClassesWithFundStructures from 'utils/assetClassesWithFundStructures';

type Props = { productUploadFormData: FormInitialData; id?: string };

let assetReferenceMap: Record<number, number> = {};

const TableControls = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-items: center;
`;

const ProductUploadInterfaceControls = styled.div`
  display: flex;
  align-items: flex-start;
  height: 100%;
  background-color: #e8edf2;

  & > div {
    margin-top: 10px;
  }
`;

const ProductUploadsHelpContent = styled.div`
  display: flex;
  & > svg {
    color: #0da4f7;
    margin-top: 2px;
    margin-right: 5px;
  }
`;

const StyledSelectionSetSelector = styled(PropertySelector)`
  && {
    padding-left: 0;
  }
`;

const StyledAddAssetsButton = styled(Button)`
  margin-left: auto;
  margin-top: 10px;
`;

const Form: React.FC<Props> = ({ productUploadFormData, id }: Props) => {
  const initialData = productUploadFormData || NullFormInitialData;

  const [selectionSet, setSelectionSet] = useState<ValueType<OptionsType, false> | undefined>();
  const [startMonth, setStartMonth] = useState<{ value: number; label: string }>(
    initialData.startMonth,
  );
  const [startYear, setStartYear] = useState<{ value: number; label: string }>(
    initialData.startYear,
  );
  const [name, setName] = useState<string>(initialData.name || '');
  const [nameIsAvailable, setNameIsAvailable] = useState<boolean>(true);
  const [endMonth, setEndMonth] = useState<{ value: number; label: string }>(initialData.endMonth);
  const [endYear, setEndYear] = useState<{ value: number; label: string }>(initialData.endYear);
  const [firmName, setFirmName] = useState<PropertyDefinition | undefined>(initialData.firmName);
  const [productName, setProductName] = useState<PropertyDefinition | undefined>(
    initialData.productName,
  );
  const [assets, setAssets] = useState<Asset[]>([...initialData.assets]);
  const [startDate, setStartDate] = useState<Date>(
    new Date(initialData.startYear.value, initialData.startMonth.value),
  );
  const [endDate, setEndDate] = useState<Date>(
    new Date(initialData.endYear.value, initialData.endMonth.value),
  );

  const [processing, setProcessing] = useState<boolean>(false);
  const [dataEnvironment] = useDataEnvironment();
  const history = useHistory();
  const initialRender = useRef<boolean>(true);

  const [showAssetLookup, setShowAssetLookup] = useState<boolean>(false);

  const { isFetching: isFetchingSelectionSets, data: selectionSets } = useGetQuery<SelectionSet[]>(
    ENDPOINTS.assetSelectionSets(dataEnvironment!.id),
  );

  const { isLoading: isLoadingPropertyDefinitions, data: propertyDefinitions } = useGetQuery<
    { value: number; label: string }[]
  >(ENDPOINTS.propertyDefinitions(dataEnvironment!.id), {
    onSuccess: (data) => {
      const noProductLabel = productName && productName.id && !productName.label;
      const noFirmLabel = firmName && firmName.id && !firmName.label;
      if (noProductLabel || noFirmLabel) {
        const selected = data.reduce(
          (acc, curr) => {
            return {
              ...acc,
              product:
                noProductLabel && curr.value === productName!.id
                  ? { id: curr.value, label: curr.label }
                  : acc.product,
              firm:
                noFirmLabel && curr.value === firmName!.id
                  ? { id: curr.value, label: curr.label }
                  : acc.firm,
            };
          },
          {
            product: productName,
            firm: firmName,
          },
        );

        const firmValue = selected.firm
          ? { id: selected.firm.id, label: selected.firm.label || 'Property not found' }
          : selected.firm;
        const productValue = selected.product
          ? { id: selected.product.id, label: selected.product.label || 'Property not found' }
          : selected.product;

        setFirmName(firmValue);
        setProductName(productValue);
      }
    },
    transformFn: (data: PropertyDefinition[]) =>
      data.map(({ id, label }) => ({
        value: id,
        label,
      })),
  });

  const setDateRangeStartMonth = (month: ValueType<OptionsType, false>) => {
    setStartMonth({ value: month!.value, label: month!.label });
    setStartDate(new Date(startDate.setMonth(month!.value)));
  };

  const setDateRangeEndMonth = (month: ValueType<OptionsType, false>) => {
    setEndMonth({ value: month!.value, label: month!.label });
    setEndDate(new Date(endDate.setMonth(month!.value)));
  };

  const setDateRangeStartYear = (year: ValueType<OptionsType, false>) => {
    setStartYear({ value: year!.value, label: year!.label });
    setStartDate(new Date(startDate.setFullYear(year!.value)));
  };

  const setDateRangeEndYear = (year: ValueType<OptionsType, false>) => {
    setEndYear({ value: year!.value, label: year!.label });
    setEndDate(new Date(endDate.setFullYear(year!.value)));
  };

  const isDateRangeValid = useMemo(() => isBefore(endDate, startDate), [startDate, endDate]);

  const mutate = usePostMutation<Asset[]>();

  const setFirmNameById = async (firmName: ValueType<OptionsType, false>) => {
    setFirmName(firmName ? { id: firmName!.value, label: firmName!.label } : undefined);
  };

  // update product/firm to use resource id hash thingy to map to values.
  const setProductNameById = async (productName: ValueType<OptionsType, false>) => {
    setProductName(productName ? { id: productName!.value, label: productName!.label } : undefined);
  };

  const [showConfirmAssetRemoval, setShowConfirmAssetRemoval] = useState<boolean>(false);
  const [resourceIdToRemove, setResourceIdToRemove] = useState<number>();
  const previousSelectionSetId = usePrevious(selectionSet ? selectionSet.value : undefined);

  const { isLoading: isFetchingAssets } = useQuery<AxiosResponse<{ assets: Asset[] }>>(
    [
      'asset_lookup',
      {
        selectionSet: selectionSet && selectionSet.value,
        firmName: firmName && firmName.id,
        productName: productName && productName.id,
      },
    ],
    {
      refetchOnWindowFocus: false,
      enabled:
        Boolean(selectionSet ? selectionSet.value : undefined !== previousSelectionSetId) ||
        assets.some((asset) => asset.resourceId),
      onSuccess: (response) => {
        const { assets: newAssets } = response.data;
        const postBody = response.config.data ? JSON.parse(response.config.data) : undefined;
        const didSelectionSetChange = Boolean(postBody && postBody.assetSelectionSetId !== null);

        if (didSelectionSetChange) {
          assetReferenceMap = newAssets.reduce((acc, curr, idx) => {
            const resourceId = curr.resourceId as number;
            acc[resourceId] = idx;
            return acc;
          }, {} as Record<number, number>);

          setAssets(newAssets);
        } else {
          const merged = [...assets];
          newAssets.forEach((newAsset) => {
            const assetIdx = assetReferenceMap[newAsset.resourceId!];
            if (assetIdx !== undefined) {
              const existingAsset = merged[assetIdx];
              merged[assetIdx] = {
                ...existingAsset,
                productName: newAsset.productName,
                firmName: newAsset.firmName,
              };
            }
          });

          setAssets([...merged]);
        }

        setProcessing(false);
      },
      queryFn: () => {
        setProcessing(true);
        const selectionSetId = selectionSet ? selectionSet.value : undefined;
        const didSelectionSetChange = Boolean(selectionSetId !== previousSelectionSetId);
        const existing = (didSelectionSetChange ? [] : assets).reduce(
          (acc: { entityIds: number[]; investmentIds: number[] }, curr) => {
            return {
              ...acc,
              entityIds: curr.entityId ? [...acc.entityIds, curr.entityId] : acc.entityIds,
              investmentIds: curr.investmentId
                ? [...acc.investmentIds, curr.investmentId]
                : acc.investmentIds,
            };
          },
          {
            investmentIds: [],
            entityIds: [],
          },
        );

        const params = {
          ...existing,
          assetSelectionSetId: didSelectionSetChange ? selectionSetId : null,
          firmNamePropertyDefinitionId: firmName && firmName.id > 0 ? firmName.id : null,
          productNamePropertyDefinitionId:
            productName && productName.id > 0 ? productName.id : null,
        };

        return axiosClient.post(ENDPOINTS.assetLookup(dataEnvironment!.id), {
          ...params,
        });
      },
      onError: (error: any) => {
        showError(
          errorMessage({
            error,
            defaultMessage: 'An error occurred when attempting to refresh asset data.',
          }),
        );
      },
    },
  );

  const hasAssets = useCallback(() => Boolean(assets && assets.some((asset) => asset.resourceId)), [
    assets,
  ]);

  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
      return;
    }

    if (!selectionSet) {
      setAssets([]);
    }
  }, [selectionSet, previousSelectionSetId]);

  const ConfirmAssetRemoval: React.FC<{}> = () => {
    return (
      <ConfirmModal
        show={showConfirmAssetRemoval}
        onClose={() => {
          setShowConfirmAssetRemoval(false);
        }}
        onConfirm={() => {
          const newAssets = assets.filter((asset) => asset.resourceId !== resourceIdToRemove);
          setAssets([...newAssets]);

          setShowConfirmAssetRemoval(false);
        }}
        title="Asset Removal"
        message="Are you sure you want to remove this asset?"
      />
    );
  };

  const handleAddDataPointValue = (key: string, resourceId: number, value: any) => {
    const index = assets.findIndex((asset) => asset.resourceId === resourceId);
    const newAssets = assets;
    const updates = { [key]: value.value };
    if (key === 'assetClassId') {
      updates['investmentFocusId'] = null;
      if (!assetClassesWithFundStructures.includes(value)) {
        updates['fundStructureId'] = null;
      }
    } else if (key === 'vehicleTypeId') {
      updates['reportingMethodId'] = null;
    }
    newAssets[index] = { ...assets[index], ...updates };
    setAssets([...newAssets]);
  };

  const onDeleteAssetRow = (resourceId: number) => {
    setResourceIdToRemove(resourceId);
    setShowConfirmAssetRemoval(true);
  };

  const handleAddAsset = (asset: Asset) => {
    const newAssets = assets.filter((assetRow) => assetRow.resourceId);
    assetReferenceMap[asset.resourceId!] = newAssets.length;
    setAssets([...newAssets, asset]);
    setShowAssetLookup(false);
  };

  const handleSaveUpload = async () => {
    const startDate = new Date(Date.UTC(startYear.value, startMonth.value + 1, 0))
      .toISOString()
      .split('T')[0];
    const endDate = new Date(Date.UTC(endYear.value, endMonth.value + 1, 0))
      .toISOString()
      .split('T')[0];

    const payload = {
      name,
      startDate,
      endDate,
      firmNamePropertyDefinitionId: firmName && firmName.id > 0 ? firmName.id : null,
      productNamePropertyDefinitionId: productName && productName.id > 0 ? productName.id : null,
      assets: assets
        .filter((asset: any) => asset.resourceId)
        .map((asset: Asset) => ({
          resourceId: asset.resourceId,
          firmName: asset.firmName,
          productName: asset.productName,
          performanceFrequency: asset.performanceFrequency,
          assetClassId: asset.assetClassId,
          investmentFocusId: asset.investmentFocusId,
          currencyCode: asset.currencyCode,
          reportingMethodId: asset.reportingMethodId,
          fundStructureId: asset.fundStructureId,
          geographicFocusId: asset.geographicFocusId,
          vehicleTypeId: asset.vehicleTypeId,
        })),
    };

    try {
      id
        ? await mutate({
            url: ENDPOINTS.productUpload(dataEnvironment!.id, id),
            payload,
            method: 'put',
          })
        : await mutate({
            url: ENDPOINTS.productUploads(dataEnvironment!.id),
            payload,
            method: 'post',
          });
      showSuccess('Product upload successfully created');
      history.push(routes.getProductUploadsRoute());
    } catch (error) {
      showError(
        errorMessage({ error, defaultMessage: 'An error initiating your upload has occurred.' }),
      );
    }
  };

  const isDirty = () => {
    const today = new Date();
    const thisMonth = today.getMonth();
    const thisYear = today.getFullYear();

    let dirty = false;

    if (id) {
      dirty =
        name !== initialData.name ||
        startMonth.value !== initialData.startMonth.value ||
        startYear.value !== initialData.startYear.value ||
        endMonth.value !== initialData.endMonth.value ||
        endYear.value !== initialData.endYear.value ||
        firmName?.id !== initialData.firmName?.id ||
        productName?.id !== initialData.productName?.id ||
        JSON.stringify(assets.filter((asset) => asset.resourceId)) !==
          JSON.stringify(initialData.assets);
    } else {
      dirty =
        (name && name.length > 0) ||
        startMonth.value !== thisMonth ||
        startYear.value !== thisYear ||
        endMonth.value !== thisMonth ||
        endYear.value !== thisYear ||
        !!firmName ||
        !!productName ||
        assets.length > 0;
    }
    return dirty;
  };

  return (
    <Fragment>
      <ConfirmAssetRemoval />

      <div style={{ paddingBottom: 100 }}>
        <ProductUploadInterfaceControls className="row pb-2 mb-2">
          <div className="col-3">
            <small>
              <label>
                Name of Product Upload <RequiredFieldMarker />
              </label>
            </small>
            <Name
              onChange={setName}
              name={name}
              initialName={initialData.name}
              available={nameIsAvailable}
              setAvailability={setNameIsAvailable}
            />
          </div>
          <div className="col-5">
            <DateRangeSelector
              errorMessage={isDateRangeValid ? 'Start date must come before end date.' : undefined}
              onChangeStartMonth={setDateRangeStartMonth}
              onChangeEndMonth={setDateRangeEndMonth}
              onChangeStartYear={setDateRangeStartYear}
              onChangeEndYear={setDateRangeEndYear}
              startMonth={startMonth}
              endMonth={endMonth}
              startYear={startYear}
              endYear={endYear}
            />
          </div>
          <div className="col-4">
            <div className="form-row property-selectors">
              <PropertySelector
                label={'Firm Name'}
                className={'col'}
                isClearable={true}
                confirmSelection={hasAssets()}
                isLoading={isLoadingPropertyDefinitions}
                options={propertyDefinitions || []}
                value={firmName ? { value: firmName.id, label: firmName.label } : undefined}
                onChange={setFirmNameById}
              />

              <PropertySelector
                label={'Product Name'}
                className={'col'}
                isClearable={true}
                confirmSelection={hasAssets()}
                isLoading={isLoadingPropertyDefinitions}
                options={propertyDefinitions || []}
                value={
                  productName ? { value: productName.id, label: productName.label } : undefined
                }
                onChange={setProductNameById}
              />
            </div>
          </div>
        </ProductUploadInterfaceControls>

        <h2
          style={{
            fontSize: 18,
            borderBottom: '2px solid #BAD9E2',
            paddingBottom: 15,
            marginTop: 15,
          }}>
          Upload Funds
        </h2>

        <ProductUploadsHelpContent>
          <Info size={'sm'} />
          <p style={{ fontSize: 13 }}>
            Most performance data in Portfolio Analytics is Net of Fees.
          </p>
        </ProductUploadsHelpContent>

        <TableControls>
          <StyledSelectionSetSelector
            className={'col-3 mb-2'}
            label={'Selection set'}
            onChange={(selection: any) => {
              if (selection) {
                setProcessing(true);
              }
              setSelectionSet(selection);
            }}
            confirmSelection={hasAssets()}
            confirmationTitle={'Change Selection Set'}
            confirmationMessage={
              'Changing the selection set will clear the assets in the table. Are you sure you wish to proceed?'
            }
            options={(selectionSets || []).map(({ id, name }) => ({
              value: id,
              label: name,
            }))}
            isLoading={isFetchingSelectionSets}
            isClearable={true}
            value={selectionSet}
          />

          <StyledAddAssetsButton
            onClick={() => {
              setShowAssetLookup(true);
              window.scrollTo({
                top: document.getElementById('asset-lookup')!.offsetTop,
                behavior: 'smooth',
              });
            }}
            variant="semitransparent"
            disabled={showAssetLookup}>
            Add Asset
          </StyledAddAssetsButton>
        </TableControls>

        <DataPoints
          assets={assets}
          onAddDataPointValue={handleAddDataPointValue}
          onDeleteAssetRow={onDeleteAssetRow}
          isLoading={isFetchingAssets || processing}
          hideNoDataMessage={showAssetLookup}
        />
        <div
          style={{
            width: '25%',
            float: 'left',
          }}>
          <AssetLookup
            onAddAsset={handleAddAsset}
            firmNamePropertyDefinitionId={firmName ? firmName.id : null}
            productNamePropertyDefinitionId={productName ? productName.id : null}
            resourceIdsToExclude={
              assets?.map((asset: Asset) => asset.resourceId).filter((resourceId) => resourceId) ??
              []
            }
            show={showAssetLookup}
          />
          <span id="asset-lookup" />
        </div>

        <div style={{ margin: '0 -15px' }}>
          <Buttons
            name={name}
            initialName={initialData.name}
            assets={assets}
            nameIsAvailable={nameIsAvailable}
            onSaveUpload={handleSaveUpload}
            dirty={isDirty()}
          />
        </div>
      </div>
    </Fragment>
  );
};

export default Form;
